'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/cn';
import { toDisplayMediaUrl } from '@/lib/media-url';

const AVATAR_PALETTE = [
  { bg: '#0F7AC7', fg: '#FFFFFF' },
  { bg: '#1F9D8B', fg: '#FFFFFF' },
  { bg: '#E36A3C', fg: '#FFFFFF' },
  { bg: '#5B6CFA', fg: '#FFFFFF' },
  { bg: '#C75A93', fg: '#FFFFFF' },
  { bg: '#7B8A2F', fg: '#FFFFFF' },
  { bg: '#7A5AF8', fg: '#FFFFFF' },
  { bg: '#CC4B4B', fg: '#FFFFFF' },
] as const;

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function isLikelyCjk(text: string): boolean {
  return /[\u3400-\u9FFF]/.test(text);
}

function getInitials(name?: string | null, email?: string | null): string {
  const normalizedName = (name || '').trim();
  if (normalizedName) {
    if (isLikelyCjk(normalizedName)) {
      return normalizedName.slice(0, 1).toUpperCase();
    }

    const parts = normalizedName.split(/[\s_-]+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    }

    return normalizedName.slice(0, 2).toUpperCase();
  }

  const emailPrefix = (email || '').split('@')[0]?.trim() || '';
  if (emailPrefix) {
    return emailPrefix.slice(0, 2).toUpperCase();
  }

  return '?';
}

function getAvatarColors(seed?: string | null) {
  if (!seed) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[hashString(seed) % AVATAR_PALETTE.length];
}

interface AvatarProps {
  src?: string | null;
  userId?: string | null;
  name?: string | null;
  email?: string | null;
  size?: number;
  className?: string;
  textClassName?: string;
  imageClassName?: string;
  alt?: string;
  title?: string;
}

export function Avatar({
  src,
  userId,
  name,
  email,
  size = 40,
  className,
  textClassName,
  imageClassName,
  alt,
  title,
}: AvatarProps) {
  const initials = getInitials(name, email);
  const colors = getAvatarColors(userId || email || name || initials);
  const label = alt || name || email || 'avatar';
  const normalizedSrc = useMemo(() => {
    const trimmed = src?.trim();
    return trimmed ? toDisplayMediaUrl(trimmed) : null;
  }, [src]);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [normalizedSrc]);

  const shouldShowImage = !!normalizedSrc && !imageFailed;

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        className,
      )}
      style={{ width: size, height: size, backgroundColor: shouldShowImage ? undefined : colors.bg, color: colors.fg }}
      title={title}
      aria-label={label}
    >
      {shouldShowImage ? (
        <img
          src={normalizedSrc}
          alt={label}
          className={cn('h-full w-full object-cover', imageClassName)}
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className={cn('font-semibold uppercase tracking-[-0.02em]', textClassName)}>
          {initials}
        </span>
      )}
    </div>
  );
}
